import { useMemo } from 'react'
import { useGetDomainsInvalidRate } from 'Features/home/api/getDomainsInvalidRate'
import { useUserStore } from 'Stores/userStoreModuleFed'
import { reduceBotzappingData } from 'Features/home/routes/Home/utils'
import { MONTH_AND_A_HALF, getCountryValueByCountryLabel, toUnixDateRangeNew } from 'Utils/dates'
import useOverallDomainPerformanceStore from 'Features/home/store/overallDomainPerformance'
import { differenceInCalendarDays, getUnixTime } from 'date-fns'
import { calcPercentage } from 'Utils/number'
import { Domain } from 'Types/Domain'
import { BotzappingDomainSummary } from '../api/types'
import { replaceWWW } from 'Utils/stringHelper'
import { useGetBotMitigationData } from '../api/getBotMitigationData'
import { mapBotzappingData } from '../components/OverallDomainsPreformance/utils'
import { ReducedResult } from '../routes/Home/types'
import _ from 'lodash'
import { useGetPaginatedDomainsByStatus } from 'Features/home/components/OverallDomainsPreformance/api/useGetPaginatedDomainsByStatus'

export interface DomainWithTrends extends Domain {
  botMitigationInvalidTrend: number
  paidMarketingInvalidTrend: number
  bmSummary: ReducedResult | undefined
}

export const useActiveDomainsWithTrends = (
  currentPage: number,
  limit: number,
  debouncedSearch: string
) => {
  const { selectedDates, sortDomainsBy, searchText, sortDirection } =
    useOverallDomainPerformanceStore()
  const { timezoneCountry } = useUserStore()

  const selectedTimezone = useMemo(() => {
    return getCountryValueByCountryLabel(timezoneCountry)
  }, [timezoneCountry])

  const serverDateConversion = toUnixDateRangeNew(selectedTimezone, selectedDates)

  //DOMAINS
  const { data: domains, isLoading: isDomainsLoading } = useGetPaginatedDomainsByStatus('active', {
    page: currentPage,
    search: debouncedSearch || '',
    limit: limit,
  })

  // BOT MITIGATION POISONED AREA
  const { data: botMititgaionData, isLoading: isBmLoading } = useGetBotMitigationData({
    domains: [],
    startDate: serverDateConversion ? serverDateConversion?.from : 0,
    endDate: serverDateConversion ? serverDateConversion?.to : 0,
  })

  const bmParsedData = useMemo(() => {
    if (botMititgaionData) {
      const fromDate = selectedDates.from ? getUnixTime(selectedDates.from) : 0
      const prev = botMititgaionData.datedSummaries.filter(
        (item) => Date.parse(item.date) < fromDate * 1000 && item.domain != ''
      )
      const curr = botMititgaionData.datedSummaries.filter(
        (item) => Date.parse(item.date) >= fromDate * 1000 && item.domain != ''
      )

      const calculatedReducedBmData = reduceBotzappingData({
        datedSummary: botMititgaionData.datedSummaries,
        insights: botMititgaionData.insights,
      })

      const mappedPrev = mapBotzappingData(curr)
      const mappedCurr = mapBotzappingData(prev)
      return {
        prev: mappedPrev,
        curr: mappedCurr,
        summary: calculatedReducedBmData,
      }
    } else {
      return {
        prev: [],
        curr: [],
      }
    }
  }, [botMititgaionData, selectedDates])

  const getBotzappingDomainWithInvalidRate = (
    domain: Domain,
    bmData: BotzappingDomainSummary[]
  ) => {
    const matchingDomainBotzapping = bmData.find(
      (summary) =>
        replaceWWW(summary.domain)?.toLowerCase() === replaceWWW(domain.address)?.toLowerCase()
    )

    const invalidRateBotzapping = matchingDomainBotzapping
      ? calcPercentage(matchingDomainBotzapping.totalBlocked, matchingDomainBotzapping.totalVisits)
      : 0
    return { domain: matchingDomainBotzapping, invalidRate: invalidRateBotzapping }
  }

  const getInvalidRateForDomain = (domain: Domain) => {
    const { prev, curr } = bmParsedData
    const currPeriod = getBotzappingDomainWithInvalidRate(domain, curr)
    const prevPeriod = getBotzappingDomainWithInvalidRate(domain, prev)
    return Math.floor(calcPercentage(currPeriod.invalidRate, prevPeriod.invalidRate)) - 100
  }
  // END OF LEPROSORIUM

  // returns previous range of dates for given date range
  const previousDateRange = useMemo(() => {
    const { from, to } = selectedDates
    if (!from || !to) {
      return { from: undefined, to: undefined }
    }

    const diffInDays: number = differenceInCalendarDays(from, to)
    const moreThan45Days = diffInDays > MONTH_AND_A_HALF

    if (moreThan45Days) {
      // if more than 45 days, we don't check trends as it take too much time
      return {
        from: getUnixTime(from),
        to: getUnixTime(to),
      }
    }

    return {
      from: getUnixTime(new Date(from.getTime() - (to.getTime() - from.getTime()))),
      to: getUnixTime(from),
    }
  }, [selectedDates])

  const { data: domainsInvalidRate, isLoading: isCurrentInvalidRateLoading } =
    useGetDomainsInvalidRate({
      campaigns: [],
      platforms: [],
      startDate: serverDateConversion?.from,
      endDate: serverDateConversion?.to,
      timezone: selectedTimezone,
    })
  const { data: previousDomainsInvalidRate, isLoading: isPreviousInvalidRateLoading } =
    useGetDomainsInvalidRate({
      campaigns: [],
      platforms: [],
      startDate: previousDateRange?.from,
      endDate: previousDateRange?.to,
      timezone: selectedTimezone,
    })

  const getPMInvalidRate = (domainID: number) => {
    const currentPeriodRate = domainsInvalidRate?.find((domain) => domain.domainId === domainID)
    const previousPeriodRate = previousDomainsInvalidRate?.find(
      (domain) => domain.domainId === domainID
    )

    if (!currentPeriodRate || !previousPeriodRate) {
      return 0
    }
    const currentRate = calcPercentage(
      currentPeriodRate.paidInvalidTraffic,
      currentPeriodRate.totalPaidTraffic
    )
    const previousRate = calcPercentage(
      previousPeriodRate.paidInvalidTraffic,
      previousPeriodRate.totalPaidTraffic
    )
    const precentageOfInvalidRate = Math.floor(calcPercentage(currentRate, previousRate))
    const changeFromPreviousMonth =
      precentageOfInvalidRate === 0 ? 0 : precentageOfInvalidRate - 100
    return changeFromPreviousMonth
  }

  const activeDomainsWithInvalidRate: DomainWithTrends[] = useMemo(() => {
    const domainsWithData =
      domains?.data?.domains?.map((domain) => ({
        ...domain,
        botMitigationInvalidTrend: getInvalidRateForDomain(domain),
        paidMarketingInvalidTrend: getPMInvalidRate(domain.tagId),
        bmSummary: bmParsedData.summary,
      })) || []

    if (searchText) {
      const filteredDomains = domainsWithData?.filter((domain) =>
        domain.address.toLowerCase().includes(searchText.toLowerCase())
      )
      return filteredDomains || []
    }

    const sortedDomainsWithData = sortDomainsBy
      ? _.orderBy(domainsWithData, sortDomainsBy, sortDirection)
      : domainsWithData

    return sortedDomainsWithData
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    bmParsedData,
    sortDomainsBy,
    searchText,
    sortDirection,
    serverDateConversion?.from,
    serverDateConversion?.to,
    domains,
  ])
  const isDomainsDataLoading =
    isCurrentInvalidRateLoading || isPreviousInvalidRateLoading || isDomainsLoading || isBmLoading

  const activeDomainsArrayWithAddedData = useMemo(() => {
    return {
      data: {
        metadata: domains?.data.metadata,
        domains: activeDomainsWithInvalidRate,
      },
    }
  }, [domains, activeDomainsWithInvalidRate])

  return {
    isDomainsDataLoading,
    activeDomainsArrayWithAddedData,
  }
}
